/**
 * Utility functions for URL detection and conversion
 */

// Regular expression to detect URLs in text
const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;

/**
 * Converts plain URLs in text to markdown link format
 * @param text - The text that may contain plain URLs
 * @returns Text with URLs converted to markdown links
 */
export function convertUrlsToMarkdown(text: string): string {
    // Strip bold markers wrapping bare URLs (e.g. **https://foo.com/** → https://foo.com/)
    text = text.replace(/\*\*(https?:\/\/[^\s*]+?)\*\*/g, '$1');

    // First, temporarily replace existing markdown links to protect them
    const markdownLinkRegex = /\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g;
    const markdownLinks: string[] = [];
    let tempText = text.replace(markdownLinkRegex, (match) => {
        const placeholder = `__MARKDOWN_LINK_${markdownLinks.length}__`;
        markdownLinks.push(match);
        return placeholder;
    });

    // Now convert plain URLs to markdown links
    tempText = tempText.replace(URL_REGEX, (url) => {
        // Clean up the URL by removing trailing punctuation that's likely not part of the URL
        const cleanedUrl = url.replace(/[.,;:!?]+$/, '');
        const trailingPunctuation = url.slice(cleanedUrl.length);

        // Create markdown link with the cleaned URL
        return `[${cleanedUrl}](${cleanedUrl})${trailingPunctuation}`;
    });

    // Restore the original markdown links
    markdownLinks.forEach((link, index) => {
        tempText = tempText.replace(`__MARKDOWN_LINK_${index}__`, link);
    });

    return tempText;
}

/**
 * Checks if a string contains any URLs
 * @param text - The text to check
 * @returns True if the text contains URLs
 */
export function containsUrls(text: string): boolean {
    // Reset the regex lastIndex to avoid issues with global flag
    URL_REGEX.lastIndex = 0;
    return URL_REGEX.test(text);
}

/**
 * Extracts all URLs from a text string
 * @param text - The text to extract URLs from
 * @returns Array of URLs found in the text
 */
export function extractUrls(text: string): string[] {
    const matches = text.match(URL_REGEX);
    return matches || [];
}

/**
 * Check if a URL matches a pattern with wildcard support
 * Pattern examples:
 * - "www.skello.io/blog*" matches "www.skello.io/blog/page"
 * - "*.skello.io" matches "blog.skello.io"
 * - "www.skello.io/page/admin*" matches "www.skello.io/page/admin/settings"
 * - "www.example.com/page" matches "www.example.com/page?query=value" (query params ignored)
 *
 * @param url - The URL to check
 * @param pattern - The pattern with optional wildcards (*)
 * @returns True if the URL matches the pattern
 */
export function matchesUrlPattern(url: string, pattern: string): boolean {
    // Skip empty or whitespace-only patterns
    if (!pattern || !pattern.trim()) {
        return false;
    }

    // Strip query parameters and hash from URL for matching (unless pattern contains ? or #)
    let urlToMatch = url;
    if (!pattern.includes('?') && !pattern.includes('#')) {
        try {
            const urlObj = new URL(url);
            urlToMatch = urlObj.origin + urlObj.pathname;
        } catch {
            // If URL parsing fails, just strip from ? or #
            urlToMatch = url.split('?')[0].split('#')[0];
        }
    }

    // Normalize trailing slashes so "https://example.com" matches "https://example.com/"
    // For wildcard patterns: only strip the pattern's trailing slash, keep URL slash so "/*" still matches root
    // For exact patterns: strip from both sides
    const hasWildcard = pattern.includes('*');
    if (!hasWildcard) {
        urlToMatch = urlToMatch.replace(/\/$/, '');
    }
    const trailingSlashStripped = hasWildcard ? pattern : pattern.replace(/\/$/, '');

    // Normalize www: strip "www." from both URL and pattern so that
    // https://hexa.com/companies* matches https://www.hexa.com/companies and vice versa
    urlToMatch = urlToMatch.replace(/^(https?:\/\/)www\./i, '$1');
    const normalizedPattern = trailingSlashStripped.replace(/^(https?:\/\/)www\./i, '$1');

    // Escape special regex characters except *
    const escapedPattern = normalizedPattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    // Replace * with regex pattern to match any characters
    const regexPattern = escapedPattern.replace(/\*/g, '.*');
    // Create regex with start and end anchors
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(urlToMatch);
}

/**
 * Append email parameter to a URL
 *
 * Used for booking flow to pre-fill email in calendar/demo booking forms.
 * Safely handles both absolute and relative URLs.
 *
 * @param url - The base URL to append email to
 * @param email - The email address to append
 * @param paramName - The query parameter name to use (default: 'email')
 * @returns URL with email parameter appended
 *
 * @example
 * appendEmailToUrl('https://calendly.com/demo', 'user@example.com')
 * // Returns: 'https://calendly.com/demo?email=user%40example.com'
 *
 * appendEmailToUrl('https://example.com/book?plan=pro', 'user@example.com')
 * // Returns: 'https://example.com/book?plan=pro&email=user%40example.com'
 */
export function appendEmailToUrl(url: string, email: string, paramName: string = 'email'): string {
    try {
        // Use window.location.href as base to support relative URLs
        const urlObj = new URL(url, window.location.href);
        urlObj.searchParams.set(paramName, email);
        return urlObj.toString();
    } catch {
        // Fallback for invalid URLs: simple string concatenation
        // Handle URLs with hash fragments by inserting query params before the hash
        const [base, hash = ''] = url.split('#');
        const separator = base.includes('?') ? '&' : '?';
        const query = `${separator}${paramName}=${encodeURIComponent(email)}`;
        return hash ? `${base}${query}#${hash}` : `${base}${query}`;
    }
}
